export type AgentCapabilitiesState = {
  enableTools: boolean;
  enableSkills: boolean;
  enableSubagents: boolean;
  enableKnowledgeBase: boolean;
};

/** Shown whenever a capability toggle causes the base prompt to be resynced. */
export const BASE_PROMPT_SYNC_NOTICE = {
  title: 'Base prompt updated',
  description: 'Updated to reflect the capabilities you changed.',
};

/** Shown when every capability toggle is off (and as the first line of the full default). */
export const DEFAULT_BASE_PROMPT = 'You are an agent. Understand the goal and act on it.';
const CAPABILITIES_INTRO = 'Use the capabilities available to you as needed.';

/** Same order as the Configure toggles: Knowledge Base, Tools, Skills, Sub agents. */
const CAPABILITY_ORDER = [
  'enableKnowledgeBase',
  'enableTools',
  'enableSkills',
  'enableSubagents',
] as const satisfies ReadonlyArray<keyof AgentCapabilitiesState>;

const CAPABILITY_LINES: Record<keyof AgentCapabilitiesState, string> = {
  enableKnowledgeBase: '- Knowledge base may be available. Search it when relevant.',
  enableTools: '- Tools may be available. Use them when helpful.',
  enableSkills: '- Skills may be available. Apply them when relevant.',
  enableSubagents: '- Sub-agents may be available. Delegate when appropriate.',
};

const KNOWN_TEMPLATE_LINES = new Set<string>([
  DEFAULT_BASE_PROMPT,
  CAPABILITIES_INTRO,
  ...Object.values(CAPABILITY_LINES),
]);

/** Generic, capability-driven default prompt. Same shape for every agent. */
export const computeDefaultBasePrompt = (capabilities: AgentCapabilitiesState): string => {
  const lines = CAPABILITY_ORDER.filter((key) => capabilities[key]).map((key) => CAPABILITY_LINES[key]);

  if (lines.length === 0) return DEFAULT_BASE_PROMPT;

  return [DEFAULT_BASE_PROMPT, '', CAPABILITIES_INTRO, '', ...lines].join('\n');
};

/**
 * True when the prompt is empty, or only our template sentences remain.
 * Each known sentence is replaced with a space; if trim() then leaves nothing, it still matches.
 */
export const isManagedDefaultPrompt = (prompt: string | null | undefined): boolean => {
  let remaining = prompt ?? '';
  for (const sentence of KNOWN_TEMPLATE_LINES) {
    remaining = remaining.replaceAll(sentence, ' ');
  }
  return remaining.trim().length === 0;
};

const contentLines = (prompt: string | null | undefined): string =>
  (prompt ?? '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');

/** Same sentences after trim, ignoring extra blank lines. */
export const promptContentMatches = (
  left: string | null | undefined,
  right: string | null | undefined
): boolean => contentLines(left) === contentLines(right);

/**
 * Apply the computed default only when the current text is empty, or it still matches the
 * previous/next default after trim. Removed lines do not match, so they are not put back.
 * A user-authored prompt is never overwritten.
 */
export const resolveSyncedBasePrompt = ({
  proposedBasePrompt,
  prevCapabilities,
  nextCapabilities,
}: {
  proposedBasePrompt: string | null | undefined;
  prevCapabilities?: AgentCapabilitiesState;
  nextCapabilities: AgentCapabilitiesState;
}): string => {
  const nextDefault = computeDefaultBasePrompt(nextCapabilities);
  if (!(proposedBasePrompt ?? '').trim()) return nextDefault;

  const prevDefault = computeDefaultBasePrompt(prevCapabilities ?? nextCapabilities);
  if (
    promptContentMatches(proposedBasePrompt, nextDefault) ||
    promptContentMatches(proposedBasePrompt, prevDefault)
  ) {
    return nextDefault;
  }

  return proposedBasePrompt ?? '';
};
